<template>
  <v-container fluid class="teacher-dash">
    <page-skeleton v-if="loading" variant="dashboard" class="mt-4" />

    <template v-else>
      <!-- Card 86akqxu0h: formulário pendente em destaque no início. -->
      <pending-surveys-card profile="partner" />

      <!-- Card 17trt6jd8uu (retorno de 26/10): campanha de coins em destaque. -->
      <active-campaigns-card profile="partner" />

      <!-- ============ HERÓI: o dia de ensino ============ -->
      <div class="td-hero">
        <div>
          <div class="td-eyebrow">{{ greeting }}, {{ teacherName }}</div>
          <div class="td-hero__line">
            <span class="td-display td-hero__num">{{ scheduledToday }}</span>
            {{ $tc("partnerDash.lessonsToday", scheduledToday) }}
            <span class="td-hero__sep">·</span>
            <span class="td-hero__ref">{{ referenceLabel }}</span>
          </div>
          <div class="td-hero__sub">
            <template v-if="attentionCount">
              <v-icon size="16" color="#FF5D2A" class="mr-1"
                >mdi-alert-circle-outline</v-icon
              >{{
                $tc("partnerDash.overdue", attentionCount, {
                  count: attentionCount,
                })
              }}
            </template>
            <template v-else>
              {{ $t("partnerDash.allClear") }}
            </template>
          </div>
        </div>

        <v-btn
          depressed
          rounded
          class="td-hero__cta"
          @click="$router.push('/connect/professor/agenda')"
        >
          <v-icon left small>mdi-calendar-month-outline</v-icon>
          {{ $t("partnerDash.seeSchedule") }}
        </v-btn>
      </div>

      <!-- ============ AULAS DE HOJE ============ -->
      <div class="td-today">
        <div class="td-today__head">
          <span class="td-section-title">{{
            $t("partnerDash.todayLessons")
          }}</span>
          <span class="td-today__weekday">{{ todayLabel }}</span>
        </div>

        <div v-if="todayClasses.length" class="td-today__rail">
          <div
            v-for="cls in todayClasses"
            :key="cls.key"
            class="td-today__slot"
            :class="{ 'td-today__slot--off': cls.cancelled }"
            @click="openClass(cls)"
          >
            <span class="td-display td-today__hour">{{ cls.hour }}</span>
            <span class="td-today__cod">{{ cls.cod }}</span>
            <span class="td-today__level">{{ cls.levelLabel }}</span>
            <span class="td-today__count">{{ cls.meta }}</span>
          </div>
        </div>

        <div v-else class="td-note">
          {{ $t("partnerDash.noLessons") }}
        </div>

        <!-- Card 17trt6jfxt2: aulas encerradas sem presença confirmada. -->
        <button
          v-if="pendingAttendanceCount"
          type="button"
          class="td-pending"
          @click="openPendingAttendance"
        >
          <v-icon size="18" color="#FF5D2A">mdi-account-check-outline</v-icon>
          <span>{{
            $tc("partnerDash.pendingAttendance", pendingAttendanceCount, {
              count: pendingAttendanceCount,
            })
          }}</span>
          <span class="td-pending__cta">{{
            $t("partnerDash.registerAttendance")
          }}</span>
        </button>
      </div>

      <!-- ============ NÚMEROS DO MÊS (retorno de 27/10) ============ -->
      <div class="td-kpis">
        <div
          v-for="kpi in kpis"
          :key="kpi.key"
          class="td-kpi"
          :class="kpi.tone"
        >
          <span class="td-kpi__icon" aria-hidden="true">
            <v-icon size="20" color="currentColor">{{ kpi.icon }}</v-icon>
          </span>
          <span class="td-kpi__label">{{ kpi.label }}</span>
          <span class="td-display td-kpi__value">{{ kpi.value }}</span>
          <span v-if="kpi.meta" class="td-kpi__meta">{{ kpi.meta }}</span>
        </div>
      </div>

      <!-- ============ COORTE (Escada) + SELOS ============ -->
      <!-- Card 86aknf6a0: os boxes ficam em linhas com a mesma altura. Retorno
           de 27/10: os selos ocupam o lugar das notificações, e as
           notificações o das cobranças recentes. -->
      <v-row dense class="mt-4">
        <!-- Coorte -->
        <v-col cols="12" lg="7" class="td-col">
          <div class="td-card td-card--pad td-fill">
            <div class="td-section-head">
              <div>
                <div class="td-section-title">
                  {{ $t("partnerDash.cohortTitle") }}
                </div>
                <div class="td-section-sub">
                  {{ $t("partnerDash.cohortSub", { count: totalStudents }) }}
                </div>
              </div>
              <v-btn
                text
                small
                class="td-linkbtn"
                @click="$router.push('/connect/professor/turmas')"
                >{{ $t("partnerDash.classes") }}</v-btn
              >
            </div>

            <div v-if="totalStudents" class="td-cohort">
              <div
                v-for="bucket in cohort"
                :key="`b-${bucket.level}`"
                class="td-cbar"
                :title="`${bucket.label}: ${bucket.count}`"
              >
                <div class="td-cbar__track">
                  <div
                    class="td-cbar__fill"
                    :class="{
                      'td-cbar__fill--top': bucket.count === maxBucket,
                    }"
                    :style="{ height: `${bucket.pct}%` }"
                  >
                    <span v-if="bucket.count" class="td-cbar__n">{{
                      bucket.count
                    }}</span>
                  </div>
                </div>
                <span class="td-cbar__tick">{{ bucket.short }}</span>
              </div>
            </div>

            <div v-else class="td-note">
              {{ $t("partnerDash.cohortEmpty") }}
            </div>
          </div>
        </v-col>

        <v-col cols="12" lg="5" class="td-col">
          <badges-card profile="partner" class="td-fill td-badges" />
        </v-col>
      </v-row>

      <!-- ============ NOTIFICAÇÕES + FINANCEIRO ============ -->
      <v-row dense class="mt-4">
        <v-col cols="12" lg="7" class="td-col">
          <!-- Notificações (card 86akj8ptp): prévia só com o título, as
               fixadas primeiro. Retorno de 27/10: no lugar das cobranças
               recentes. -->
          <notifications-preview
            profile="partner"
            :accent="accent"
            class="td-fill"
          />
        </v-col>

        <!-- Financeiro: a avaliação do mês em três números, e a carteira. -->
        <v-col cols="12" lg="5" class="td-col">
          <div class="td-side td-fill">
            <div class="td-stats td-stats--three">
              <div class="td-stat">
                <span class="td-stat__label">{{
                  $t("partnerDash.toReceive")
                }}</span>
                <span class="td-stat__value">{{
                  formatCurrency(toReceiveAmount)
                }}</span>
                <span class="td-stat__meta">{{
                  $tc("partnerDash.pendingCount", toReceiveCount, {
                    count: toReceiveCount,
                  })
                }}</span>
              </div>
              <div class="td-stat td-stat--paid">
                <span class="td-stat__label">{{
                  $t("partnerDash.received")
                }}</span>
                <span class="td-stat__value">{{
                  formatCurrency(revenue.totalPaidAmount)
                }}</span>
                <span class="td-stat__meta">{{
                  $tc("partnerDash.paidCount", revenue.paidCount, {
                    count: revenue.paidCount,
                  })
                }}</span>
              </div>
              <div class="td-stat td-stat--overdue">
                <span class="td-stat__label">{{
                  $t("partnerDash.overdueLabel")
                }}</span>
                <span class="td-stat__value">{{
                  formatCurrency(overdueAmount)
                }}</span>
                <span class="td-stat__meta">{{
                  $tc("partnerDash.overdueCountMeta", overdueCount, {
                    count: overdueCount,
                  })
                }}</span>
              </div>
            </div>

            <div class="td-card td-card--pad td-forecast">
              <div class="td-wallet">
                <div class="td-wallet__item">
                  <span class="td-wallet__label">{{
                    $t("partnerDash.available")
                  }}</span>
                  <strong class="td-wallet__value">{{
                    formatCurrency(wallet.cashBalance)
                  }}</strong>
                </div>
                <!-- Card 17trt6jd8uu: quantidade de coins, com link para
                     o extrato. -->
                <router-link
                  to="/connect/professor/pitch-coins"
                  class="td-wallet__item td-wallet__item--coins"
                >
                  <span class="td-wallet__label">Pitch Coins</span>
                  <strong class="td-wallet__value">{{
                    $t("pitchCoins.amountCoins", { amount: coinsAvailable })
                  }}</strong>
                </router-link>
              </div>
              <v-btn
                block
                text
                class="td-forecast__link"
                @click="$router.push('/connect/professor/financeiro')"
              >
                {{ $t("partnerDash.openWallet") }}
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import NotificationsPreview from "@/components/notifications/NotificationsPreview.vue";
import PendingSurveysCard from "@/components/surveys/PendingSurveysCard.vue";
import BadgesCard from "@/components/coins/BadgesCard.vue";
import ActiveCampaignsCard from "@/components/coins/ActiveCampaignsCard.vue";
import { classThemes } from "@/shared/classThemes";
import { formatBRL } from "@/plugins/i18n";
import { formatCoins } from "@/shared/coins";
import {
  ATTENDANCE_REFRESH_EVENT,
  dispatchAttendanceOpen,
  loadPendingAttendance,
} from "@/components/attendance/attendanceEvents";

const LEVEL_LABELS = [
  "Beginner 1",
  "Beginner 2",
  "Essential 1",
  "Essential 2",
  "Pre-Intermediate 1",
  "Pre-Intermediate 2",
  "Intermediate 1",
  "Intermediate 2",
  "Upper 1",
  "Upper 2",
  "Advanced 1",
  "Advanced 2",
];
const LEVEL_SHORT = [
  "B1",
  "B2",
  "E1",
  "E2",
  "P1",
  "P2",
  "I1",
  "I2",
  "U1",
  "U2",
  "A1",
  "A2",
];

// Dia da semana (0=Domingo..6=Sábado) no fuso oficial da plataforma
// (America/Sao_Paulo). Os horários das turmas são em horário de Brasília, então
// "hoje" precisa ser o dia de Brasília — e não o do dispositivo do professor,
// que pode estar em outro fuso e contar as aulas no dia errado (card 86ajdhzvk).
const SP_WEEKDAY_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};
function saoPauloWeekdayIndex(date = new Date()) {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    weekday: "short",
  }).format(date);
  const index = SP_WEEKDAY_INDEX[weekday];
  return index === undefined ? date.getDay() : index;
}

// Data de hoje em Brasília no formato yyyy-MM-dd, que é como o backend serializa
// o início de cada evento (`start: "yyyy-MM-dd HH:mm"`). en-CA já entrega nessa
// ordem, evitando remontar a string.
function saoPauloToday(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

// Agora em Brasília no formato dos eventos (yyyy-MM-dd HH:mm), para comparar
// com o `start` da agenda, que é horário de parede.
function saoPauloNow(date = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

export default {
  name: "PartnerDashboard",

  components: {
    NotificationsPreview,
    PendingSurveysCard,
    ActiveCampaignsCard,
    BadgesCard,
  },

  data() {
    return {
      loading: true,
      pendingAttendanceCount: 0,
      groups: [],
      events: [],
      dashboard: {
        reference: { label: "" },
        wallet: { cashBalance: 0, coinBalance: 0 },
        revenue: {
          pendingCount: 0,
          paidCount: 0,
          totalPendingAmount: 0,
          totalPaidAmount: 0,
          totalExpectedAmount: 0,
        },
      },
      coins: null,
    };
  },

  async created() {
    window.addEventListener(ATTENDANCE_REFRESH_EVENT, this.loadPendingCount);
    this.loadPendingCount();
    await this.loadAll();
  },

  beforeDestroy() {
    window.removeEventListener(ATTENDANCE_REFRESH_EVENT, this.loadPendingCount);
  },

  computed: {
    accent() {
      return classThemes.partner.primary;
    },
    // Saldo do extrato de coins; a wallet (cache) so se a leitura falhar.
    coinsAvailable() {
      return formatCoins(
        this.coins ? this.coins.available : this.wallet.coinBalance
      );
    },
    teacherName() {
      return (
        this.$store.getters.getName?.split(" ")[0] ||
        this.$t("partnerDash.teacherFallback")
      );
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return this.$t("common.goodMorning");
      if (h < 18) return this.$t("common.goodAfternoon");
      return this.$t("common.goodEvening");
    },
    referenceLabel() {
      return (
        this.dashboard.reference?.label || this.$t("partnerDash.thisMonth")
      );
    },
    wallet() {
      return this.dashboard.wallet || { cashBalance: 0, coinBalance: 0 };
    },
    revenue() {
      return (
        this.dashboard.revenue || {
          pendingCount: 0,
          paidCount: 0,
          totalPendingAmount: 0,
          totalPaidAmount: 0,
          totalExpectedAmount: 0,
        }
      );
    },
    // A avaliação do mês: "a receber" é o que ainda não venceu, para o atrasado
    // não contar duas vezes.
    overdueCount() {
      return Number(this.revenue.overdueCount || 0);
    },
    overdueAmount() {
      return Number(this.revenue.totalOverdueAmount || 0);
    },
    toReceiveCount() {
      return Math.max(
        0,
        Number(this.revenue.pendingCount || 0) - this.overdueCount
      );
    },
    toReceiveAmount() {
      return Math.max(
        0,
        Number(this.revenue.totalPendingAmount || 0) - this.overdueAmount
      );
    },
    attentionCount() {
      return this.overdueCount;
    },
    // Aulas do mês que já passaram e não foram canceladas (a mesma regra do
    // relatório do administrativo).
    lessonsHeld() {
      const now = saoPauloNow();
      const month = now.slice(0, 7);

      return this.events.filter((event) => {
        const start = String(event.start || "");
        return (
          start.slice(0, 7) === month && start <= now && event.color !== "red"
        );
      }).length;
    },
    kpis() {
      return [
        {
          key: "students",
          icon: "mdi-account-group-outline",
          tone: "td-kpi--grape",
          label: this.$t("partnerDash.kpiStudents"),
          value: this.totalStudents,
        },
        {
          key: "classes",
          icon: "mdi-google-classroom",
          tone: "td-kpi--teal",
          label: this.$t("partnerDash.kpiClasses"),
          value: this.activeGroups.length,
        },
        {
          key: "lessons",
          icon: "mdi-check-circle-outline",
          tone: "td-kpi--ember",
          label: this.$t("partnerDash.kpiLessons"),
          value: this.lessonsHeld,
          meta: this.$t("partnerDash.kpiLessonsMeta"),
        },
        {
          key: "forecast",
          icon: "mdi-cash-multiple",
          tone: "td-kpi--grape",
          label: this.$t("partnerDash.kpiForecast"),
          value: this.formatCurrency(this.revenue.totalExpectedAmount),
        },
      ];
    },
    activeGroups() {
      return this.groups.filter((g) => Number(g.status) === 1);
    },
    todayLabel() {
      return this.$t(`enums.weekDayMedium.${saoPauloWeekdayIndex()}`);
    },
    // Aulas de hoje vindas da agenda real, não da grade recorrente da turma.
    // A grade (weekDays/hours) só diz em que dia da semana a turma costuma
    // acontecer: ela não sabe de turma quinzenal, aula adiada para outro dia,
    // aula cancelada nem de curso que já terminou — e por isso mostrava aulas
    // que não ocorrem hoje. Os eventos do calendário são a fonte da verdade.
    todayClasses() {
      const today = saoPauloToday();

      return this.events
        .filter((event) => String(event.start || "").slice(0, 10) === today)
        .map((event) => {
          // Aula cancelada continua na agenda marcada de vermelho; ela aparece
          // no dia, mas como aviso — não conta como aula a dar.
          const cancelled = event.color === "red";
          const students = Array.isArray(event.students)
            ? event.students.length
            : 0;

          if (event.isGeneralClass) {
            return {
              key: `general-${event.generalClassId}`,
              generalClassId: event.generalClassId,
              cod: event.name,
              hour: String(event.start).slice(11, 16),
              levelLabel: event.audienceLabel || event.levelLabel || "",
              meta: cancelled
                ? this.$t("partnerDash.cancelled")
                : event.generalClassTypeLabel ||
                  this.$t("partnerDash.conversation"),
              cancelled,
            };
          }

          return {
            key: `class-${event.id}`,
            groupId: event.classId,
            cod: event.code || event.name,
            hour: String(event.start).slice(11, 16),
            levelLabel: LEVEL_LABELS[Number(event.level)] || "",
            meta: cancelled
              ? this.$t("partnerDash.cancelled")
              : this.$tc("partnerDash.students", students, { count: students }),
            cancelled,
          };
        })
        .sort((a, b) => a.hour.localeCompare(b.hour));
    },
    // O número do herói é o que o professor ainda tem para dar hoje.
    scheduledToday() {
      return this.todayClasses.filter((item) => !item.cancelled).length;
    },
    totalStudents() {
      return this.activeGroups.reduce(
        (sum, g) => sum + this.activeStudents(g),
        0
      );
    },
    // Distribuição de alunos ativos pelos 12 níveis.
    cohort() {
      const counts = new Array(12).fill(0);
      this.activeGroups.forEach((g) => {
        const lv = Number(g.level);
        if (lv >= 0 && lv < 12) counts[lv] += this.activeStudents(g);
      });
      const max = Math.max(1, ...counts);
      return counts.map((count, level) => ({
        level,
        count,
        label: LEVEL_LABELS[level],
        short: LEVEL_SHORT[level],
        pct: count ? Math.max(8, (count / max) * 100) : 0,
      }));
    },
    maxBucket() {
      return Math.max(0, ...this.cohort.map((b) => b.count));
    },
  },

  methods: {
    // Card 17trt6jfxt2: quantas aulas encerradas esperam a presença.
    async loadPendingCount() {
      const lessons = await loadPendingAttendance();
      if (lessons) this.pendingAttendanceCount = lessons.length;
    },
    openPendingAttendance() {
      dispatchAttendanceOpen();
    },
    async loadAll() {
      this.loading = true;
      try {
        const [dash, groups, calendar, coins] = await Promise.all([
          partnerApi.getDashboard(),
          partnerApi.getStudyGroups().catch(() => ({ data: [] })),
          partnerApi.calendar().catch(() => ({ data: { events: [] } })),
          partnerApi.getCoinsSummary().catch(() => null),
        ]);
        this.dashboard = { ...this.dashboard, ...(dash.data || {}) };
        this.coins = coins?.data?.data || null;
        this.groups = Array.isArray(groups.data) ? groups.data : [];
        this.events = Array.isArray(calendar.data?.events)
          ? calendar.data.events
          : [];
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: this.$t("partnerDash.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    activeStudents(group) {
      return (group.content || []).filter((s) => Number(s.status) === 1).length;
    },
    openClass(cls) {
      // Aula de conversação não tem turma: o professor a gerencia pela agenda.
      if (!cls.groupId) {
        this.$router.push("/connect/professor/agenda");
        return;
      }
      this.$router.push(`/connect/professor/turmas/${cls.groupId}`);
    },
    formatCurrency(value) {
      return formatBRL(value);
    },
  },
};
</script>

<style scoped>
.teacher-dash {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #faf7fc);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --grape: var(--dk-grape, #a826e1);
  --grape-soft: rgba(168, 38, 225, 0.1);
  --teal: var(--dk-teal, #00a379);
  --ember: var(--dk-ember, #ff5d2a);
  padding: 8px 4px 40px;
  color: var(--ink);
}

.td-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

.td-eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--grape);
}

.td-section-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.td-section-sub {
  margin-top: 3px;
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.4;
}
.td-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}
.td-linkbtn.v-btn,
.td-forecast__link.v-btn {
  color: var(--grape) !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.td-card {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.td-card--pad {
  padding: 22px;
}
.td-pending {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--dk-line-strong, #ffd2c2);
  background: var(--dk-ember-wash, #fff4ef);
  color: var(--ink);
  font-size: 0.88rem;
  text-align: left;
  cursor: pointer;
}

.td-pending__cta {
  margin-left: auto;
  font-weight: 700;
  color: #a826e1;
  white-space: nowrap;
}

.td-note {
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.5;
}

/* ---------- Herói ---------- */
.td-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 12px;
  padding: 26px 28px;
  border-radius: 24px;
  background: radial-gradient(
      120% 150% at 100% 0%,
      rgba(168, 38, 225, 0.1),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
}
.td-hero__line {
  margin-top: 8px;
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--ink);
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}
.td-hero__num {
  font-size: 2.6rem;
  font-weight: 700;
  color: var(--grape);
  line-height: 1;
}
.td-hero__sep {
  color: var(--line);
}
.td-hero__ref {
  font-size: 1rem;
  font-weight: 600;
  color: var(--muted);
  text-transform: capitalize;
}
.td-hero__sub {
  margin-top: 10px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: var(--muted);
}
.td-hero__cta.v-btn {
  background: linear-gradient(
    160deg,
    var(--dk-grape, #a826e1),
    var(--dk-grape-deep, #771ba0)
  ) !important;
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
  box-shadow: 0 16px 30px -16px rgba(168, 38, 225, 0.8);
}

/* ---------- Aulas de hoje ---------- */
.td-today {
  margin-top: 16px;
  padding: 20px 22px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.td-today__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
}
.td-today__weekday {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--muted);
}
.td-today__rail {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.td-today__slot {
  flex: 0 0 auto;
  min-width: 150px;
  padding: 14px 16px;
  border-radius: 16px;
  background: var(--paper);
  border: 1px solid var(--line);
  border-left: 3px solid var(--grape);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
}
.td-today__slot:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 26px -18px rgba(168, 38, 225, 0.6);
}
.td-today__slot--off {
  border-left-color: var(--ember);
  opacity: 0.62;
}
.td-today__slot--off .td-today__hour {
  text-decoration: line-through;
}
.td-today__slot--off .td-today__count {
  color: var(--ember);
  font-weight: 700;
}
.td-today__hour {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1;
}
.td-today__cod {
  margin-top: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--ink);
}
.td-today__level {
  font-size: 0.78rem;
  color: var(--grape);
  font-weight: 600;
}
.td-today__count {
  margin-top: 4px;
  font-size: 0.76rem;
  color: var(--muted);
}

/* ---------- Coorte (Escada) ---------- */
.td-cohort {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 170px;
  padding-top: 8px;
}
.td-cbar {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}
.td-cbar__track {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.td-cbar__fill {
  width: 72%;
  min-height: 3px;
  border-radius: 7px 7px 3px 3px;
  background: linear-gradient(
    180deg,
    var(--dk-grape, #c56eeb),
    var(--dk-grape, #a826e1)
  );
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  transition: height 0.5s ease;
}
.td-cbar__fill--top {
  background: linear-gradient(
    180deg,
    var(--dk-grape, #a826e1),
    var(--dk-grape-deep, #771ba0)
  );
}
.td-cbar__n {
  position: absolute;
  top: -18px;
  font-size: 0.72rem;
  font-weight: 800;
  color: var(--ink);
}
.td-cbar__tick {
  margin-top: 8px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--muted);
}

/* ---------- Financeiro ---------- */
.td-side {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 16px;
}

/* ---------- Grade de boxes (card 86aknf6a0) ---------- */
/* Cada coluna estica o card até a altura da linha, então os boxes lado a
   lado terminam na mesma linha. */
.td-col {
  display: flex;
  flex-direction: column;
}
.td-fill {
  flex: 1 1 auto;
}
.td-card.td-fill {
  display: flex;
  flex-direction: column;
}
/* O gráfico da coorte ocupa a altura que sobrar ao lado das notificações. */
.td-card.td-fill > .td-cohort {
  flex: 1 1 auto;
  height: auto;
  min-height: 170px;
}
.td-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.td-stat {
  padding: 16px 18px;
  border-radius: 18px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-top: 3px solid var(--ember);
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
  display: flex;
  flex-direction: column;
}
.td-stat--paid {
  border-top-color: var(--teal);
}
.td-stat--overdue {
  border-top-color: var(--dk-red, #d52323);
}
/* Três números lado a lado; o valor encolhe um pouco para caber na coluna. */
.td-stats--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.td-stats--three .td-stat {
  padding: 14px;
}
.td-stats--three .td-stat__value {
  font-size: 1.15rem;
  overflow-wrap: anywhere;
}

/* ---------- Números do mês (retorno de 27/10) ---------- */
.td-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.td-kpi {
  --kpi: var(--grape);
  position: relative;
  /* Retorno de 27/10: o número fica ao lado do ícone, o rótulo embaixo. */
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: 14px;
  padding: 18px 20px 16px;
  border-radius: 20px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.td-kpi--teal {
  --kpi: var(--teal);
}
.td-kpi--ember {
  --kpi: var(--ember);
}
.td-kpi__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  color: var(--kpi);
  background: var(--grape-soft);
  background: color-mix(in srgb, var(--kpi) 14%, transparent);
}
.td-kpi__icon {
  grid-area: 1 / 1;
}
.td-kpi__value {
  grid-area: 1 / 2;
}
.td-kpi__label,
.td-kpi__meta {
  grid-column: 1 / -1;
}
.td-kpi__label {
  margin-top: 14px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}
.td-kpi__value {
  margin-top: 0;
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.td-kpi__meta {
  margin-top: 4px;
  font-size: 0.76rem;
  color: var(--muted);
}

/* Os selos ocupam a coluna inteira, como os outros boxes da linha. */
.td-badges {
  margin: 0;
}
.td-stat__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}
.td-stat__value {
  margin-top: 8px;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--ink);
}
.td-stat__meta {
  margin-top: 4px;
  font-size: 0.76rem;
  color: var(--muted);
}

.td-wallet {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 14px 0;
}
.td-wallet__item {
  padding: 14px;
  border-radius: 14px;
  background: var(--grape-soft);
}
.td-wallet__item--coins {
  background: rgba(59, 130, 246, 0.09);
  text-decoration: none;
  transition: background 0.16s ease;
}
.td-wallet__item--coins:hover,
.td-wallet__item--coins:focus-visible {
  background: rgba(59, 130, 246, 0.16);
}
.td-wallet__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--muted);
}
.td-wallet__value {
  display: block;
  margin-top: 6px;
  font-size: 1.15rem;
  color: var(--ink);
}
.td-forecast__link.v-btn {
  margin-top: 2px;
}

/* ---------- Responsivo ---------- */
@media (max-width: 960px) {
  .td-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .td-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .td-stats--three {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .td-cbar__fill,
  .td-today__slot {
    transition: none;
  }
}
</style>
